import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronLeft, ChevronRight, X } from "lucide-react";
import { formatDate, money } from "../lib/api";

export const MediaViewer = ({ workshop, onClose, onBook }) => {
  const media = workshop.media?.length
    ? workshop.media
    : [{ kind: "image", url: workshop.image }];
  const [index, setIndex] = useState(0);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const go = (dir) => setIndex((i) => (i + dir + media.length) % media.length);
  const current = media[index];

  return (
    <motion.div
      data-testid="media-viewer"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 40 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl bg-white rounded-[28px] overflow-hidden shadow-2xl"
      >
        <div className="flex items-center justify-between px-6 py-4">
          <div className="min-w-0">
            <h3 className="font-display font-extrabold text-lg tracking-tight truncate">{workshop.title}</h3>
            <p className="text-xs font-medium text-neutral-500">{formatDate(workshop.date)} · {money(workshop.price_cents)}</p>
          </div>
          <button
            data-testid="media-viewer-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center hover:border-black transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="relative bg-[#141412]">
          <div key={index} className="flex items-center justify-center h-[52vh] sm:h-[58vh]">
            {current.kind === "video" ? (
              <video
                data-testid={`media-viewer-video-${index}`}
                src={current.url}
                controls
                autoPlay
                playsInline
                className="w-full h-full object-contain"
              />
            ) : (
              <img
                data-testid={`media-viewer-image-${index}`}
                src={current.url}
                alt={`${workshop.title} photo ${index + 1}`}
                className="w-full h-full object-contain"
              />
            )}
          </div>
          {media.length > 1 && (
            <>
              <button
                data-testid="media-viewer-prev"
                onClick={() => go(-1)}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                data-testid="media-viewer-next"
                onClick={() => go(1)}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-lg hover:bg-white transition-colors"
              >
                <ChevronRight size={18} />
              </button>
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 px-6 py-4">
          <div className="flex items-center gap-1.5" data-testid="media-viewer-dots">
            {media.map((m, i) => (
              <button
                key={i}
                data-testid={`media-viewer-dot-${i}`}
                onClick={() => setIndex(i)}
                aria-label={`Show ${m.kind} ${i + 1}`}
                className={`h-2 rounded-full transition-all duration-300 ${
                  i === index ? "w-6 bg-[#1d1d1b]" : "w-2 bg-neutral-300 hover:bg-neutral-400"
                }`}
              />
            ))}
          </div>
          <button
            data-testid="media-viewer-book"
            onClick={() => onBook(workshop)}
            className="btn-dark inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold shrink-0"
          >
            Book Your Spot <ArrowRight size={14} />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};
